/**
 * Custom hook for company search autocomplete
 *
 * Debounced search hook that queries the companies API
 * for use in autocomplete dropdowns.
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { CompanyResponse } from "@/types/api/admin";

// ============================================================================
// QUERY KEYS
// ============================================================================

const COMPANY_SEARCH_QUERY_KEYS = {
  all: ["company-search"] as const,
  search: (term: string) => [...COMPANY_SEARCH_QUERY_KEYS.all, term] as const,
};

// ============================================================================
// SEARCH HOOK
// ============================================================================

/**
 * Hook for searching companies via autocomplete
 *
 * @param searchTerm - Search string (min 2 chars to trigger)
 * @param enabled - Whether the query should be enabled
 *
 * Queries GET /companies?search=...&limit=10
 */
export function useCompanySearch(searchTerm: string, enabled: boolean = true) {
  return useQuery({
    queryKey: COMPANY_SEARCH_QUERY_KEYS.search(searchTerm),
    queryFn: async (): Promise<CompanyResponse[]> => {
      return await apiGet<CompanyResponse[]>("/companies", {
        params: { search: searchTerm, limit: 10 },
      });
    },
    enabled: enabled && searchTerm.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}
